import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms } from '../shaders/safeZone.glsl.js';
import { artProxyUrl } from '../artwork-texture.js';

// Shared-rig contract: forbidden. The picture is a full-frame field of flat
// indexed colour. A deck wash has nothing to light and would only raise the
// black level of an image that is already covering every pixel.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'cycle',
  family: 'shader',
  danceability: 0.80,
  energyLevel: 0.76,
  bpmMin: 110,
  bpmMax: 175,
  movement: 0.70,
  tonality: 0.60,
  prefers: { gridStrength: [0.55, 1.0] },
  enabled: true
};

// Cycle: the album cover reduced to a 32-entry indexed palette whose colours
// turn on the beat, over a plasma field that warps it. Nothing moves,
// everything flows.
//
// This is the demoscene's colour cycling, from the days when a palette was a
// hardware register file: an image stored as indices, and a palette rotated
// one step at a time. The gradients start to flow without a single pixel
// changing place. It is the most hypnotic effect of that era and it is nearly
// free - two texture reads per pixel.
//
// EVERYTHING TURNS ON THE PALETTE BEING A RING. Colour 31 must lead back into
// colour 0 as naturally as 7 leads into 8, or a seam walks across the picture
// once per revolution. That is why the palette is not merely sorted by hue:
// it is ordered as a closed TOUR (nearest neighbour plus 2-opt on a hue-led
// distance), and then RESAMPLED at thirty-two equal steps along that closed
// curve, so every step is the same small step - including the last one.
// `?cyclepalette=1` draws the ring as a band and is the acceptance test.

const N_PAL = 32;
const K_TAILLE = 128;          // la pochette est quantifiée sur 128 x 128
const SAT_MIN = 0.70;          // une palette terne ne coule pas
const CRETE = 0.85;
const LUMA = 'vec3(0.2125, 0.7154, 0.0721)';
const PLANCHER_PARTAGE = 0.32;
const PLANCHER = 0.50;
// LE PLASMA est lent et la palette est rapide : c'est ce contraste qui fait
// l'effet. Amplitude faible, sans quoi la pochette n'est plus reconnaissable.
const PLASMA_AMPL = 0.02;
const GRAIN = 0.03;
const DECALAGE_2 = N_PAL / 2;
const REGIMES = ['plasma', 'rings', 'bars', 'none'];
const DEBUT_CRAN_MS = 500;     // sans bpm : un cran toutes les 500 ms
const INDEX_MAX_PX = 2304;     // borne de la texture d'index, par côté

const TIERS = {
  2: { doubleCycle: true, dither: true, grain: true, regimes: REGIMES },
  1: { doubleCycle: false, dither: true, grain: true, regimes: REGIMES },
  0: { doubleCycle: false, dither: false, grain: false, regimes: ['plasma', 'none'] }
};

const clamp01 = (x) => (x < 0 ? 0 : x > 1 ? 1 : x);
function urlParam(nom) {
  if (typeof window === 'undefined' || !window.location) return null;
  return new URLSearchParams(window.location.search).get(nom);
}

// --- conversions -----------------------------------------------------------
function rgbVersHsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const mx = Math.max(r, g, b), mn = Math.min(r, g, b), d = mx - mn;
  let h = 0;
  if (d > 1e-9) {
    if (mx === r) h = ((g - b) / d + 6) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  const l = (mx + mn) / 2;
  const s = d < 1e-9 ? 0 : d / (1 - Math.abs(2 * l - 1));
  return [h, Math.min(1, s), l];
}
function hslVersRgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h * 6) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  const k = Math.floor(h * 6) % 6;
  if (k === 0) { r = c; g = x; } else if (k === 1) { r = x; g = c; }
  else if (k === 2) { g = c; b = x; } else if (k === 3) { g = x; b = c; }
  else if (k === 4) { r = x; b = c; } else { r = c; b = x; }
  return [Math.round((r + m) * 255), Math.round((g + m) * 255), Math.round((b + m) * 255)];
}
// L'écart de teinte est CIRCULAIRE : 0.98 et 0.02 sont voisins, pas opposés.
const ecartTeinte = (a, b) => { const d = Math.abs(a - b); return d > 0.5 ? 1 - d : d; };
// La distance qui ORDONNE l'anneau : menée par la teinte, lissée par la
// luminance. C'est le « parcours le plus court par teinte » du brief, et le
// poids de la luminance est ce qui évite qu'un pas saute du sombre au clair
// à teinte égale.
function distanceAnneau(a, b) {
  const dh = ecartTeinte(a[0], b[0]) * 2;   // 0..1
  const dl = a[2] - b[2];
  const ds = a[1] - b[1];
  return Math.sqrt(dh * dh + 0.45 * dl * dl + 0.12 * ds * ds);
}

// --- k-means ---------------------------------------------------------------
// Une passe rapide sur la version 128 x 128. k-means++ pour l'initialisation :
// tiré au hasard, deux amas voisins se partagent souvent le même centre et la
// palette rend deux fois la même couleur, ce qui creuse un trou dans l'anneau.
function kmeans(pixels, k, iterations) {
  const n = pixels.length / 4;
  const ech = [];
  for (let i = 0; i < n; i++) {
    if (pixels[i * 4 + 3] < 8) continue;
    ech.push([pixels[i * 4], pixels[i * 4 + 1], pixels[i * 4 + 2]]);
  }
  if (!ech.length) return [];
  let graine = 12345;
  const rnd = () => { graine = (graine * 1103515245 + 12345) % 2147483648; return graine / 2147483648; };
  const centres = [ech[Math.floor(rnd() * ech.length)].slice()];
  const d2 = new Float64Array(ech.length).fill(Infinity);
  while (centres.length < k) {
    const c = centres[centres.length - 1];
    let somme = 0;
    for (let i = 0; i < ech.length; i++) {
      const e = ech[i];
      const d = (e[0] - c[0]) ** 2 + (e[1] - c[1]) ** 2 + (e[2] - c[2]) ** 2;
      if (d < d2[i]) d2[i] = d;
      somme += d2[i];
    }
    if (somme <= 0) { centres.push(ech[Math.floor(rnd() * ech.length)].slice()); continue; }
    let cible = rnd() * somme, i = 0;
    while (i < ech.length - 1 && (cible -= d2[i]) > 0) i++;
    centres.push(ech[i].slice());
  }
  const attrib = new Int32Array(ech.length);
  for (let it = 0; it < iterations; it++) {
    for (let i = 0; i < ech.length; i++) {
      const e = ech[i];
      let best = 0, bd = Infinity;
      for (let c = 0; c < centres.length; c++) {
        const q = centres[c];
        const d = (e[0] - q[0]) ** 2 + (e[1] - q[1]) ** 2 + (e[2] - q[2]) ** 2;
        if (d < bd) { bd = d; best = c; }
      }
      attrib[i] = best;
    }
    const somme = centres.map(() => [0, 0, 0, 0]);
    for (let i = 0; i < ech.length; i++) {
      const s = somme[attrib[i]], e = ech[i];
      s[0] += e[0]; s[1] += e[1]; s[2] += e[2]; s[3]++;
    }
    for (let c = 0; c < centres.length; c++) {
      if (!somme[c][3]) { centres[c] = ech[Math.floor(rnd() * ech.length)].slice(); continue; }
      centres[c] = [somme[c][0] / somme[c][3], somme[c][1] / somme[c][3], somme[c][2] / somme[c][3]];
    }
  }
  return centres;
}

// --- l'anneau --------------------------------------------------------------
// Un TOUR fermé, pas un tri. Trier par teinte laisse des sauts de luminance
// entre deux entrées de teinte voisine, et surtout ne dit rien de la jonction
// 31 -> 0. Le plus proche voisin donne un tour correct, 2-opt le décroise :
// sur trente-deux points c'est une poignée de microsecondes.
function tourFerme(hsl) {
  const n = hsl.length;
  const vus = new Uint8Array(n);
  const tour = [0]; vus[0] = 1;
  for (let k = 1; k < n; k++) {
    const c = hsl[tour[tour.length - 1]];
    let best = -1, bd = Infinity;
    for (let i = 0; i < n; i++) {
      if (vus[i]) continue;
      const d = distanceAnneau(c, hsl[i]);
      if (d < bd) { bd = d; best = i; }
    }
    tour.push(best); vus[best] = 1;
  }
  // 2-opt sur un tour FERMÉ : on inverse un segment tant que ça raccourcit.
  const d = (a, b) => distanceAnneau(hsl[tour[a % n]], hsl[tour[b % n]]);
  let ameliore = true, gardes = 0;
  while (ameliore && gardes++ < 200) {
    ameliore = false;
    for (let i = 0; i < n - 1; i++) {
      for (let j = i + 2; j < n; j++) {
        if (i === 0 && j === n - 1) continue;
        const avant = d(i, i + 1) + d(j, j + 1);
        const apres = d(i, j) + d(i + 1, j + 1);
        if (apres < avant - 1e-9) {
          let a = i + 1, b = j;
          while (a < b) { const t = tour[a]; tour[a] = tour[b]; tour[b] = t; a++; b--; }
          ameliore = true;
        }
      }
    }
  }
  return tour;
}

// Le ré-échantillonnage : on parcourt le tour fermé à pas d'arc CONSTANT et
// on prend trente-deux points. C'est lui qui « interpole entre entrées
// consécutives trop éloignées » - et il le fait sans avoir à choisir un seuil,
// puisque tous les pas deviennent égaux par construction. L'interpolation est
// en HSL le long du plus court arc de teinte : en RGB, deux couleurs opposées
// passent par le gris et l'anneau se troue.
function reechantillonner(hsl, tour, n) {
  const pts = tour.map((i) => hsl[i]);
  const m = pts.length;
  const seg = [];
  let total = 0;
  for (let i = 0; i < m; i++) {
    const l = distanceAnneau(pts[i], pts[(i + 1) % m]);
    seg.push(l); total += l;
  }
  if (total < 1e-6) return pts.slice(0, n);
  const out = [];
  let idx = 0, reste = 0;
  for (let k = 0; k < n; k++) {
    let cible = (k / n) * total;
    idx = 0; reste = cible;
    while (idx < m - 1 && reste > seg[idx]) { reste -= seg[idx]; idx++; }
    const t = seg[idx] > 1e-9 ? reste / seg[idx] : 0;
    const a = pts[idx], b = pts[(idx + 1) % m];
    let dh = b[0] - a[0];
    if (dh > 0.5) dh -= 1; else if (dh < -0.5) dh += 1;
    out.push([(a[0] + dh * t + 1) % 1, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]);
  }
  return out;
}

// La palette complète, depuis les pixels d'une pochette. Exportée : le test du
// critère 1 n'a besoin ni de navigateur ni de GPU.
export function construirePalette(pixels) {
  let centres = kmeans(pixels, N_PAL, 8);
  if (!centres.length) return paletteDeRepli();
  let hsl = centres.map((c) => rgbVersHsl(c[0], c[1], c[2]));
  // La saturation plancher AVANT la mise en anneau : elle change les teintes
  // utiles d'une entrée grise, et l'ordre doit se faire sur les couleurs
  // qu'on affichera réellement.
  hsl = hsl.map((c) => [c[0], Math.max(SAT_MIN, c[1]), Math.min(0.88, Math.max(0.12, c[2]))]);
  const anneau = reechantillonner(hsl, tourFerme(hsl), N_PAL);
  const out = new Uint8Array(N_PAL * 4);
  for (let i = 0; i < N_PAL; i++) {
    const [r, g, b] = hslVersRgb(anneau[i][0], anneau[i][1], anneau[i][2]);
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b; out[i * 4 + 3] = 255;
  }
  return out;
}

// Sans pochette : un anneau de teintes, construit depuis vividColors si on en
// a, sinon la roue complète. Le même ré-échantillonnage s'applique, donc la
// continuité de la jonction est garantie de la même façon.
export function paletteDeRepli(vividColors) {
  const hsl = [];
  if (vividColors && vividColors.length) {
    for (const v of vividColors) {
      const c = [(v >> 16) & 255, (v >> 8) & 255, v & 255];
      const h = rgbVersHsl(c[0], c[1], c[2]);
      hsl.push([h[0], Math.max(SAT_MIN, h[1]), Math.min(0.85, Math.max(0.15, h[2]))]);
    }
  }
  while (hsl.length < 8) hsl.push([hsl.length / 8, 0.80, 0.30 + 0.35 * ((hsl.length * 5) % 7) / 7]);
  const anneau = reechantillonner(hsl, tourFerme(hsl), N_PAL);
  const out = new Uint8Array(N_PAL * 4);
  for (let i = 0; i < N_PAL; i++) {
    const [r, g, b] = hslVersRgb(anneau[i][0], anneau[i][1], anneau[i][2]);
    out[i * 4] = r; out[i * 4 + 1] = g; out[i * 4 + 2] = b; out[i * 4 + 3] = 255;
  }
  return out;
}

// La mesure du critère 1 : les pas d'une entrée à la suivante, la jonction
// 31 -> 0 COMPRISE. Un anneau continu a un pas maximal proche de son pas
// moyen, et la jonction ne doit pas être le plus grand de tous.
export function mesurerAnneau(pal) {
  const hsl = [];
  for (let i = 0; i < N_PAL; i++) hsl.push(rgbVersHsl(pal[i * 4], pal[i * 4 + 1], pal[i * 4 + 2]));
  const pas = [];
  for (let i = 0; i < N_PAL; i++) pas.push(distanceAnneau(hsl[i], hsl[(i + 1) % N_PAL]));
  const moy = pas.reduce((a, b) => a + b, 0) / N_PAL;
  const max = Math.max(...pas);
  let rangJonction = 1;
  for (const p of pas) if (p > pas[N_PAL - 1] + 1e-12) rangJonction++;
  return { moyen: moy, max: max, jonction: pas[N_PAL - 1], rangJonction: rangJonction, pas: pas };
}

// --- LA TEXTURE D'INDEX ----------------------------------------------------
// La matrice de Bayer 8 x 8, seuils ordonnés dans [0,1). C'est elle qui donne
// les dégradés en escalier caractéristiques - et c'est elle qui rend le
// cycling VISIBLE : sans tramage, une zone plate reste un seul index et ne
// coule pas du tout, elle change de couleur d'un bloc.
const BAYER = (() => {
  const m = [[0, 32, 8, 40, 2, 34, 10, 42], [48, 16, 56, 24, 50, 18, 58, 26],
             [12, 44, 4, 36, 14, 46, 6, 38], [60, 28, 52, 20, 62, 30, 54, 22],
             [3, 35, 11, 43, 1, 33, 9, 41], [51, 19, 59, 27, 49, 17, 57, 25],
             [15, 47, 7, 39, 13, 45, 5, 37], [63, 31, 55, 23, 61, 29, 53, 21]];
  const out = new Float32Array(64);
  for (let y = 0; y < 8; y++) for (let x = 0; x < 8; x++) out[y * 8 + x] = m[y][x] / 64;
  return out;
})();

// Le pas moyen de la palette en RGB : l'amplitude du tramage doit valoir
// environ un pas, sans quoi il ne fait rien (trop petit) ou il détruit
// l'image (trop grand).
function pasRgbMoyen(pal) {
  let s = 0;
  for (let i = 0; i < N_PAL; i++) {
    const j = (i + 1) % N_PAL;
    s += Math.hypot(pal[i * 4] - pal[j * 4], pal[i * 4 + 1] - pal[j * 4 + 1], pal[i * 4 + 2] - pal[j * 4 + 2]);
  }
  return s / N_PAL;
}

// --- LES SHADERS -----------------------------------------------------------

const ZONE_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  float zoneSecurite(vec2 s) {
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    float a = mix(${PLANCHER_PARTAGE.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d));
    return ${PLANCHER.toFixed(2)} + (a - ${PLANCHER_PARTAGE.toFixed(2)})
         * ${((1 - PLANCHER) / (1 - PLANCHER_PARTAGE)).toFixed(6)};
  }
`;

const VERT = `
  varying vec2 vUv;
  varying vec2 vNdc;
  void main() {
    vUv = uv;
    vNdc = position.xy * 2.0;   // le quad va de -0.5 à 0.5
    gl_Position = vec4(position.xy * 2.0, 0.0, 1.0);
  }
`;

const FRAG = `
  precision mediump float;
  ${ZONE_CHUNK}
  uniform sampler2D uIndex;
  uniform sampler2D uPalette;
  uniform float uShift;
  uniform float uTemps;
  uniform float uAmpl;        // amplitude du plasma, modulée par bands.low
  uniform float uRegime;      // 0 plasma, 1 rings, 2 bars, 3 none
  uniform float uEscalier;    // bands.high : rend le tramage plus visible
  uniform float uDouble;      // opacité de la seconde couche
  uniform float uDecalage;    // son décalage de palette, en crans
  uniform float uGrain;
  uniform float uAspect2;
  varying vec2 vUv;
  varying vec2 vNdc;

  // LA DÉFORMATION. Elle bouge les UV de LECTURE, jamais un pixel : l'image
  // reste une image indexée, et c'est la palette qui coule. En régime NONE il
  // n'y a aucune déformation du tout et pourtant l'image coule toujours -
  // c'est la démonstration que le mouvement ne vient pas d'elle.
  vec2 deformation(vec2 uv) {
    if (uRegime > 2.5) return vec2(0.0);
    float t = uTemps;
    if (uRegime > 1.5) {
      // BARS : des bandes horizontales.
      return vec2(sin(uv.y * 30.0 + t) * uAmpl, 0.0);
    }
    if (uRegime > 0.5) {
      // RINGS : des anneaux concentriques depuis le centre.
      vec2 c = vec2((uv.x - 0.5) * uAspect2, uv.y - 0.5);
      float r = length(c);
      return (r > 1e-4 ? c / r : vec2(0.0)) * sin(r * 20.0 - t) * uAmpl;
    }
    // PLASMA : le champ classique, TROIS sinus et un seul scalaire. J'en
    // avais d'abord écrit deux composantes, donc six sinus - mesuré sur le Pi,
    // le plasma coûtait 2,5 ms sur un cadre plein, la moitié du shader. Un
    // seul décalage, appliqué aux deux axes en quadrature, donne le même
    // brassage pour la moitié du prix.
    float o = sin(uv.y * 8.0 + t) + sin(uv.x * 6.0 - t * 0.7) + sin((uv.x + uv.y) * 5.0 + t * 1.3);
    return vec2(o, o * 0.62) * uAmpl * 0.334;
  }

  // Un hachage SANS sinus : la scène couvre tout le cadre, et un sinus par
  // pixel sur une surface pleine se paie - mesuré ailleurs à près de trois
  // millisecondes sur le Pi pour un demi-million de pixels.
  float bruit(vec2 p) {
    float a = fract(dot(p, vec2(0.1031, 0.1030)));
    a = fract(a * 43.5453 + p.x * 0.0031 + p.y * 0.0017);
    return fract(a * 17.13);
  }

  float lireIndex(vec2 uv) { return texture2D(uIndex, uv).r * 255.0; }
  vec3 couleur(float idx) {
    return texture2D(uPalette, vec2((idx + uShift + 0.5) / ${N_PAL.toFixed(1)}, 0.5)).rgb;
  }

  void main() {
    vec2 uv = vUv + deformation(vUv);
    // LE TRAMAGE VIF. Le tramage de Bayer est cuit dans la texture d'index au
    // changement de morceau ; il ne peut donc pas suivre les aigus. Celui-ci
    // perturbe l'INDEX à l'affichage, ce qui rend l'escalier plus ou moins
    // marqué sans rien recalculer. Ordonné, comme l'autre.
    float idx = lireIndex(uv);
    // DERRIÈRE UNE VRAIE CONDITION. Multiplier le hachage par zéro ne coûte
    // pas zéro : le hachage s'exécute quand même, sur chaque pixel du cadre.
    if (uEscalier > 0.001) idx += (bruit(floor(gl_FragCoord.xy * 0.5)) - 0.5) * uEscalier;
    vec3 col = couleur(idx);

    // LE DOUBLE CYCLING. La même image lue deux fois plus petite et tournée
    // d'un quart de tour, décalée d'un demi-tour de palette, mélangée en
    // MULTIPLY : les deux cycles se croisent et produisent des moirés de
    // couleur qui n'existent dans aucune des deux couches.
    if (uDouble > 0.001) {
      vec2 uv2 = vec2(uv.y, 1.0 - uv.x) * 2.0;
      // LES DEUX INDEX S'AJOUTENT AVANT LA LECTURE DE PALETTE. C'est le
      // mécanisme des démos d'époque, et c'est le seul qui produise un vrai
      // moiré de COULEUR : une petite variation du second index fait sauter
      // d'une entrée de palette à l'autre, donc d'une teinte franche à une
      // autre. Mélanger deux COULEURS ne le fait pas - mesuré, le multiply du
      // brief assombrissait de 14,3/255 pour un écart-type spatial de 2,3,
      // c'est-à-dire un voile uniforme et aucun motif.
      float i2 = lireIndex(uv2);
      vec3 c2 = texture2D(uPalette,
        vec2((idx + i2 - 2.0 * uShift + uDecalage + 0.5) / ${N_PAL.toFixed(1)}, 0.5)).rgb;
      col = mix(col, c2, uDouble);
    }

    col *= zoneSecurite(vNdc);
    // LE GRAIN, multiplicatif : additif, il remonterait le noir - la moitié
    // positive de l'oscillation éclaircit bien plus que la négative
    // n'assombrit, une fois passée la courbe de sortie.
    if (uGrain > 0.001) col *= 1.0 + (bruit(gl_FragCoord.xy + uTemps * 37.0) - 0.5) * uGrain;

    // Un plafond de LUMINANCE. Des aplats saturés plein cadre qui bloomeraient
    // rendraient l'image entière laiteuse.
    float l = dot(col, ${LUMA});
    if (l > ${CRETE.toFixed(2)}) col *= ${CRETE.toFixed(2)} / max(l, 1e-4);
    gl_FragColor = vec4(col, 1.0);
  }
`;

// La bande de palette du drapeau ?cyclepalette=1 : trente-deux aplats côte à
// côte, plus la jonction 31 -> 0 répétée à droite pour qu'on la VOIE.
const BANDE_FRAG = `
  precision mediump float;
  uniform sampler2D uPalette;
  uniform float uShift;
  varying vec2 vUv;
  void main() {
    float u = vUv.x * ${(N_PAL + 8).toFixed(1)};
    float idx = floor(u);
    gl_FragColor = vec4(texture2D(uPalette,
      vec2((idx + uShift + 0.5) / ${N_PAL.toFixed(1)}, 0.5)).rgb, 1.0);
  }
`;

// ---------------------------------------------------------------------------

const _taille = new THREE.Vector2();

export class CycleVisualizer extends Visualizer {
  constructor(options) {
    super();
    const o = options || {};
    this._tier = o.qualityTier != null ? o.qualityTier : 1;
    this._plan = TIERS[this._tier] || TIERS[1];
    this._voirPalette = urlParam('cyclepalette') === '1';
    const sh = parseInt(urlParam('cycleshift'), 10);
    this._shiftForce = Number.isFinite(sh) ? sh : null;
    this._modeForce = urlParam('cyclemode');
    this._pasLibre = urlParam('cyclestep') === '1';
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    this._temps = 0;
    this._shift = 0;
    this._sens = 1;
    this._battements = 0;
    this._phaseBeat = null; this._phaseBar = null; this._phasePhrase = null;
    this._secondesBeat = 0.5;
    this._libre = 0; this._pasLibreAt = 0;
    this._regime = 0;
    this._urlVue = undefined;
    this._modePrec = null;
    this._dropJusqu = -1; this._dropShift = 0;
    this._quant = null;          // l'état du calcul différé de la texture d'index
    this._tailleIndex = { w: 0, h: 0 };
    this._imageCourante = null;
    this._pasRgb = 24;

    this._palTex = new THREE.DataTexture(paletteDeRepli(null), N_PAL, 1, THREE.RGBAFormat);
    this._palTex.colorSpace = THREE.SRGBColorSpace;
    this._palTex.magFilter = THREE.NearestFilter;
    this._palTex.minFilter = THREE.NearestFilter;
    // LA PALETTE S'ENROULE. C'est la seule raison pour laquelle le cycling
    // marche sans une seule ligne de code de bouclage : uShift peut valoir
    // n'importe quoi, la lecture retombe toujours dans l'anneau.
    this._palTex.wrapS = THREE.RepeatWrapping;
    this._palTex.wrapT = THREE.ClampToEdgeWrapping;
    this._palTex.generateMipmaps = false;
    this._palTex.needsUpdate = true;

    // Une texture d'index d'attente, unie : sans elle la première image est
    // noire le temps que la quantification se fasse.
    this._idxTex = new THREE.DataTexture(new Uint8Array([0]), 1, 1, THREE.RedFormat);
    this._idxTex.magFilter = THREE.NearestFilter;
    this._idxTex.minFilter = THREE.NearestFilter;
    this._idxTex.wrapS = THREE.MirroredRepeatWrapping;
    this._idxTex.wrapT = THREE.MirroredRepeatWrapping;
    this._idxTex.generateMipmaps = false;
    this._idxTex.needsUpdate = true;

    this._uni = {
      uAspect: safeZoneUniforms.uAspect,
      uSafeCenter: safeZoneUniforms.uSafeCenter,
      uSafeHalfSize: safeZoneUniforms.uSafeHalfSize,
      uSafeFeather: safeZoneUniforms.uSafeFeather,
      uIndex: { value: this._idxTex },
      uPalette: { value: this._palTex },
      uShift: { value: 0 },
      uTemps: { value: 0 },
      uAmpl: { value: PLASMA_AMPL },
      uRegime: { value: 0 },
      uEscalier: { value: 0 },
      uDouble: { value: this._plan.doubleCycle ? 0.30 : 0 },
      uDecalage: { value: DECALAGE_2 },
      uGrain: { value: this._plan.grain ? GRAIN : 0 },
      uAspect2: { value: 1.78 }
    };

    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = new THREE.ShaderMaterial({
      uniforms: this._uni,
      vertexShader: VERT,
      fragmentShader: this._voirPalette ? BANDE_FRAG : FRAG,
      depthTest: false, depthWrite: false, precision: 'mediump'
    });
    this._quad = new THREE.Mesh(geo, mat);
    this._quad.frustumCulled = false;
    this._quad.renderOrder = -10;
    this._scene.add(this._quad);

    const self = this;
    this._quad.onBeforeRender = function (renderer) {
      renderer.getSize(_taille);
      if (_taille.x > 0 && _taille.y > 0) {
        self._uni.uAspect2.value = _taille.x / _taille.y;
        self._verifierTaille(_taille.x, _taille.y);
      }
    };
  }

  // La taille de la texture d'index suit celle du canvas, bornée. On ne
  // recalcule QUE sur un vrai changement : le tramage de Bayer est en pixels
  // d'écran, et refaire trois millions de pixels pour deux pixels de bordure
  // serait un gaspillage visible.
  _verifierTaille(w, h) {
    const k = Math.min(1, INDEX_MAX_PX / Math.max(w, h));
    const tw = Math.max(64, Math.round(w * k)), th = Math.max(64, Math.round(h * k));
    if (Math.abs(tw - this._tailleIndex.w) < 24 && Math.abs(th - this._tailleIndex.h) < 24) return;
    this._tailleIndex = { w: tw, h: th };
    if (this._imageCourante) this._lancerQuantification();
  }

  _chargerPochette(url) {
    if (typeof document === 'undefined') return;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      this._imageCourante = img;
      try {
        // La palette, sur une version 128 x 128 : un k-means sur trois
        // millions de pixels coûterait des secondes pour le même résultat.
        const c = document.createElement('canvas');
        c.width = K_TAILLE; c.height = K_TAILLE;
        const g = c.getContext('2d', { willReadFrequently: true });
        g.drawImage(img, 0, 0, K_TAILLE, K_TAILLE);
        this._poserPalette(construirePalette(g.getImageData(0, 0, K_TAILLE, K_TAILLE).data));
      } catch (e) {
        // Canvas teinté (CORS) : on garde la palette de repli plutôt que de
        // rendre une image noire.
        this._imageCourante = null;
        return;
      }
      this._lancerQuantification();
    };
    img.onerror = () => { this._imageCourante = null; };
    img.src = artProxyUrl(url);
  }

  _poserPalette(pal) {
    this._palTex.image.data = pal;
    this._palTex.needsUpdate = true;
    this._pasRgb = pasRgbMoyen(pal);
    this._palette = pal;
  }

  // LA QUANTIFICATION EST DIFFÉRÉE. Trois millions de pixels contre trente-deux
  // couleurs font cent millions de comparaisons : fait d'un bloc, le morceau
  // saute. On en traite une tranche par image, et l'ancienne texture continue
  // de s'afficher entre-temps.
  _lancerQuantification() {
    const { w, h } = this._tailleIndex;
    if (!w || !h) return;
    let src;
    try {
      const c = document.createElement('canvas');
      c.width = w; c.height = h;
      const g = c.getContext('2d', { willReadFrequently: true });
      // SANS POCHETTE : une image de plasma PUR, quantifiée comme le serait
      // une pochette. Le visualiseur a toujours quelque chose à faire couler -
      // c'est le cas de la radio, où il n'y a souvent aucune image.
      if (!this._imageCourante) {
        const im = g.createImageData(w, h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          const u = x / w, v = y / h, i = (y * w + x) * 4;
          const f = Math.sin(v * 9.0) + Math.sin(u * 7.0) + Math.sin((u + v) * 5.5)
                  + Math.sin(Math.hypot(u - 0.5, v - 0.5) * 16.0);
          const t = (f + 4) / 8;
          im.data[i] = 255 * t; im.data[i + 1] = 255 * (1 - t);
          im.data[i + 2] = 255 * Math.abs(Math.sin(f * 1.7)); im.data[i + 3] = 255;
        }
        g.putImageData(im, 0, 0);
        src = g.getImageData(0, 0, w, h).data;
        this._quant = { src: src, out: new Uint8Array(w * h), i: 0, w: w, h: h,
                        pal: this._palette || paletteDeRepli(null) };
        return;
      }
      // COVER PAR LA HAUTEUR, puis répétition EN MIROIR sur les côtés : la
      // pochette est carrée et l'écran ne l'est pas. Le miroir est ce qui
      // remplit sans couture - une répétition simple ferait une arête franche
      // à chaque raccord, et un étirement déformerait le sujet.
      const t = h, x0 = (w - t) / 2;
      const n = Math.ceil(x0 / t) + 1;
      for (let k = -n; k <= n; k++) {
        g.save();
        const x = x0 + k * t;
        if (k % 2 === 0) g.drawImage(this._imageCourante, x, 0, t, t);
        else { g.translate(x + t, 0); g.scale(-1, 1); g.drawImage(this._imageCourante, 0, 0, t, t); }
        g.restore();
      }
      src = g.getImageData(0, 0, w, h).data;
    } catch (e) { return; }
    this._quant = { src: src, out: new Uint8Array(w * h), i: 0, w: w, h: h,
                    pal: this._palette || paletteDeRepli(null) };
  }

  _avancerQuantification(budget) {
    const q = this._quant;
    if (!q) return;
    const { src, out, w, pal } = q;
    const total = out.length;
    const fin = Math.min(total, q.i + budget);
    // L'amplitude du tramage vaut environ un PAS de palette : plus petit il ne
    // fait rien, plus grand il détruit l'image. C'est ce tramage-là qui donne
    // les dégradés en escalier, et c'est lui qui rend le cycling visible dans
    // les aplats - sans lui, une zone plate change de couleur d'un bloc.
    const ampl = this._plan.dither ? this._pasRgb * 0.55 : 0;
    for (let p = q.i; p < fin; p++) {
      const x = p % w, y = (p / w) | 0;
      const s = (BAYER[(y & 7) * 8 + (x & 7)] - 0.5) * ampl;
      const r = src[p * 4] + s, g = src[p * 4 + 1] + s, b = src[p * 4 + 2] + s;
      let best = 0, bd = Infinity;
      for (let c = 0; c < N_PAL; c++) {
        const dr = r - pal[c * 4], dg = g - pal[c * 4 + 1], db = b - pal[c * 4 + 2];
        const d = dr * dr + dg * dg + db * db;
        if (d < bd) { bd = d; best = c; }
      }
      out[p] = best;
    }
    q.i = fin;
    if (fin >= total) {
      const t = new THREE.DataTexture(out, q.w, q.h, THREE.RedFormat);
      t.magFilter = THREE.NearestFilter;
      t.minFilter = THREE.NearestFilter;
      t.wrapS = THREE.MirroredRepeatWrapping;
      t.wrapT = THREE.MirroredRepeatWrapping;
      t.generateMipmaps = false;
      t.needsUpdate = true;
      const vieille = this._idxTex;
      this._idxTex = t;
      this._uni.uIndex.value = t;
      if (vieille) vieille.dispose();
      this._quant = null;
    }
  }

  update(deltaTime, profile) {
    // PAUSE : gel du cycling. La palette reste où elle est, et l'image avec.
    if (profile.playbackState !== 'PLAYING') return;
    const dt = Math.min(deltaTime, 0.1);
    this._temps += dt;
    const u = this._uni;
    const director = profile.director;
    const bandes = (director && director.hasReliableGrid && director.bands) ? director.bands : null;
    const low = bandes ? clamp01(bandes.low) : 0;
    const high = bandes ? clamp01(bandes.high) : 0;

    if (profile.artworkUrl !== this._urlVue) {
      this._urlVue = profile.artworkUrl;
      if (profile.artworkUrl) this._chargerPochette(profile.artworkUrl);
      else {
        this._imageCourante = null;
        const p = profile.artworkPalette;
        this._poserPalette(paletteDeRepli(p && p.vividColors));
        this._lancerQuantification();
      }
    }
    // Une tranche de quantification par image. Cent-vingt mille pixels tiennent
    // largement dans le budget, et trois millions sont faits en vingt-cinq
    // images - le temps d'un fondu, pendant lequel l'ancienne image s'affiche.
    if (this._quant) this._avancerQuantification(120000);

    const bpm = (profile.trackFeatures && profile.trackFeatures.bpm) || 0;
    this._secondesBeat = bpm > 0 ? 60 / bpm : DEBUT_CRAN_MS / 1000;

    // --- LES FRONTS.
    let battement = false;
    const bp = bpm > 0 ? profile.beatPhase : null;
    if (bp != null) {
      if (this._phaseBeat != null && bp < this._phaseBeat - 0.5) battement = true;
      this._phaseBeat = bp;
    } else {
      // SANS BPM : un cran toutes les 500 ms. Le cycling ne s'arrête jamais.
      this._libre += dt;
      if (this._libre >= DEBUT_CRAN_MS / 1000) { this._libre -= DEBUT_CRAN_MS / 1000; battement = true; }
    }
    if (this._pasLibre) {
      battement = this._temps - this._pasLibreAt > 0.7;
      if (battement) this._pasLibreAt = this._temps;
    }

    let mesure = false;
    const bar = profile.barPhase;
    if (bar != null) {
      if (this._phaseBar != null && bar < this._phaseBar - 0.5) mesure = true;
      this._phaseBar = bar;
    } else if (battement && (this._battements + 1) % 4 === 0) mesure = true;

    let phrase = false;
    const ph = profile.phrasePhase;
    if (ph != null) {
      if (this._phasePhrase != null && ph < this._phasePhrase - 0.5) phrase = true;
      this._phasePhrase = ph;
    } else if (battement && (this._battements + 1) % 16 === 0) phrase = true;

    // --- LE CYCLING. Tout le mécanisme tient ici : un entier qui avance.
    const ev = director && director.nextEvent;
    // Quatre temps avant un drop connu, le cycling accélère à deux crans.
    const preRoll = !!(ev && ev.type === 'DROP' && ev.msUntil != null
      && ev.msUntil <= 4 * this._secondesBeat * 1000);
    if (battement) {
      this._battements++;
      this._shift += this._sens * ((mesure || preRoll) ? 2 : 1);
    }
    if (phrase) {
      // Le SENS s'inverse, et le régime de déformation change.
      this._sens = -this._sens;
      if (!this._modeForce) {
        const r = this._plan.regimes;
        this._regime = REGIMES.indexOf(r[(r.indexOf(REGIMES[this._regime]) + 1 + r.length) % r.length]);
        if (this._regime < 0) this._regime = 0;
      }
    }

    // --- LE DROP : la palette s'inverse d'un demi-tour pendant un temps.
    // Chaque couleur devient son opposée dans l'anneau, donc TOUTE l'image
    // change d'un coup - puis reprend exactement où elle en était.
    const mode = director ? director.mode : null;
    if (mode === 'drop' && this._modePrec !== 'drop') {
      this._dropJusqu = this._temps + this._secondesBeat;
      this._dropShift = N_PAL / 2;
    }
    this._modePrec = mode;
    if (this._temps > this._dropJusqu) this._dropShift = 0;

    let shift = this._shift + this._dropShift;
    if (this._shiftForce != null) shift = this._shiftForce;
    u.uShift.value = shift;

    // --- LA DÉFORMATION.
    if (this._modeForce) {
      const i = REGIMES.indexOf(this._modeForce);
      if (i >= 0) this._regime = i;
    }
    u.uRegime.value = this._regime;
    // Le plasma est LENT : c'est le contraste avec une palette rapide qui fait
    // l'effet. bands.low module son amplitude de plus ou moins la moitié.
    u.uTemps.value = this._temps * 0.55;
    u.uAmpl.value = PLASMA_AMPL * (1 + 0.5 * (low * 2 - 1));
    // Les aigus rendent l'escalier plus visible.
    u.uEscalier.value = this._plan.dither ? high * 1.6 : 0;
  }

  dispose() {
    if (this._quad) {
      this._quad.onBeforeRender = null;
      this._scene.remove(this._quad);
      this._quad.geometry.dispose();
      this._quad.material.dispose();
      this._quad = null;
    }
    if (this._idxTex) { this._idxTex.dispose(); this._idxTex = null; }
    if (this._palTex) { this._palTex.dispose(); this._palTex = null; }
    this._quant = null;
    this._imageCourante = null;
  }
}

export default CycleVisualizer;
